// src/components/pages/backtest/partials/BacktestDetailed.js
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from 'react-router-dom';
import Plot from "react-plotly.js";
import { useTranslation } from "react-i18next";
import { useAuth } from 'react-oidc-context';
import { toast, ToastContainer } from 'react-toastify';
import { ChevronRight } from "lucide-react";

// Components
import PerformanceTableBacktest from "./PerformanceTableBacktest";
import PerformanceTableIndividual from "./PerformanceTableIndividual";
import DataPeriodAlert from "./DataPeriodAlert";
import LoginPromptToast from '../../../partials/LoginPromptToast/LoginPromptToast';
import NewPortfolioModal from '../../portfolios/modal/NewPortfolioModal';
import ChallengeCta from "../../../partials/ChallengeCta/ChallengeCta";

// Hooks & Utils
import { useBacktestData } from "../hooks/useBacktestData";
import { getCommonLayout, getOptimizationLinks, calculateYRange, CRISIS_SCENARIOS } from "../utils/backtestUtils";

// Styles
import commonStyles from "../../../../styles/Common.module.css";
import styles from "./style/BacktestDetailed.module.css";

const ReturnsHeatmap = ({ pfData, bmData, type, t, viewMode }) => {
  if (!pfData) return null;
  
  const safeBmData = bmData || {};
  const isDiffMode = viewMode === 'difference';
  // 연도 내림차순 정렬
  const years = Object.keys(pfData).sort((a, b) => b - a);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // --- [Helper] Color Scale Logic ---
  const getCellStyle = (val, mode = 'standard') => {
    if (val === null || val === undefined) return { backgroundColor: 'transparent' };
    const percentage = val * 100;
    let bg = 'transparent';
    let color = '#333';

    if (mode === 'diff') {
      if (percentage > 0) {
        // Outperform
        const lightness = Math.max(98 - percentage * 5, 65); 
        bg = `hsl(221, 83%, ${lightness}%)`; 
        color = '#1e3a8a'; 
      } else if (percentage < 0) {
        // Underperform
        const lightness = Math.max(98 - Math.abs(percentage) * 5, 70);
        bg = `hsl(35, 85%, ${lightness}%)`;
        color = '#7c2d12';
      }
      const fontWeight = Math.abs(percentage) > 5 ? '700' : '500';
      return { backgroundColor: bg, color: color, fontWeight: fontWeight };
    }
    else {
      // Drawdown & Return Standard
      const lightness = Math.max(98 - Math.abs(percentage) * 2, 60);
      
      if (percentage >= 0) {
        // [수정] 양수(수익)일 경우: 초록색 (Hue 142)
        bg = `hsl(142, 70%, ${lightness}%)`;
        color = lightness < 70 ? '#fff' : '#14532d'; // 짙은 초록 텍스트
      } else {
        // [수정] 음수(손실)일 경우: 빨간색 (Hue 0)
        bg = `hsl(0, 80%, ${lightness}%)`;
        color = lightness < 70 ? '#fff' : '#7f1d1d'; // 짙은 빨강 텍스트
      }
    }

    return { backgroundColor: bg, color: color };
  };

  const formatVal = (val) => (val === null || val === undefined ? '-' : `${(val * 100).toFixed(1)}%`);

  // --- [Calculation] Statistics ---
  // 수정된 통계 계산 함수: 전체 평균 및 연간 평균을 정확히 계산
  const calculateStats = (dataKey, isBm = false) => {
    const sums = Array(12).fill(0);
    const counts = Array(12).fill(0);
    const winCounts = Array(12).fill(0);
    const validPairsCount = Array(12).fill(0);

    // 전체 월 평균 계산을 위한 변수
    let totalMonthlySum = 0;
    let totalMonthlyCount = 0;

    // 연간 통계(YTD 컬럼용) 계산을 위한 변수
    let totalYearlySum = 0;
    let totalYearlyCount = 0;

    years.forEach(year => {
      const pRow = pfData[year];
      const bRow = safeBmData[year];
      if (!pRow) return;

      const pVals = type === 'return' ? pRow.returns : pRow.drawdowns;
      const bVals = bRow ? (type === 'return' ? bRow.returns : bRow.drawdowns) : Array(12).fill(null);

      // 데이터 존재 여부 확인용 (수익률 기준)
      const pReturns = pRow.returns;
      const bReturns = bRow ? bRow.returns : Array(12).fill(null);

      const targetVals = isBm ? bVals : pVals;
      const targetReturns = isBm ? bReturns : pReturns;

      // 1. 월별 데이터 집계
      targetVals.forEach((val, mIdx) => {
        const isActualData = type === 'return' 
          ? (val !== null && val !== undefined)
          : (targetReturns[mIdx] !== null && targetReturns[mIdx] !== undefined);

        if (isActualData) {
          sums[mIdx] += val;
          counts[mIdx]++;
          
          // 전체 월 데이터 누적 (Arithmetic Mean)
          totalMonthlySum += val;
          totalMonthlyCount++;
        }

        // 승률 계산 (포트폴리오 vs 벤치마크)
        if (!isBm) {
          const pVal = pVals[mIdx];
          const bVal = bVals[mIdx];
          const hasBothData = pReturns[mIdx] !== null && bReturns[mIdx] !== null; // 둘 다 데이터가 있을 때만

          if (hasBothData) {
            validPairsCount[mIdx]++;
            if (pVal > bVal) winCounts[mIdx]++;
          }
        }
      });

      // 2. 연간 데이터(YTD) 집계
      let yearlyVal = null;
      if (type === 'return') {
          // 수익률 모드: 연간 Total Return
          yearlyVal = isBm ? bRow?.totalReturn : pRow.totalReturn;
      } else {
          // 낙폭 모드: 연간 Max Drawdown
          const rowDrawdowns = isBm ? bRow?.drawdowns : pRow.drawdowns;
          if (rowDrawdowns) {
              const validDDs = rowDrawdowns.filter(v => v !== null && v !== undefined);
              if (validDDs.length > 0) {
                  yearlyVal = Math.min(...validDDs);
              }
          }
      }

      if (yearlyVal !== null && yearlyVal !== undefined) {
          totalYearlySum += yearlyVal;
          totalYearlyCount++;
      }
    });

    return {
      // 월별 평균
      avgs: sums.map((s, i) => counts[i] ? s / counts[i] : null),
      // 승률
      winRates: winCounts.map((w, i) => validPairsCount[i] ? w / validPairsCount[i] : null),
      // 전체 월평균 (모든 월 데이터를 합산하여 나눔)
      totalMonthlyAvg: totalMonthlyCount > 0 ? totalMonthlySum / totalMonthlyCount : null,
      // 연평균 (YTD 컬럼의 값들을 합산하여 나눔)
      avgYearlyTotal: totalYearlyCount > 0 ? totalYearlySum / totalYearlyCount : null
    };
  };

  const pfStats = calculateStats();
  const bmStats = calculateStats(null, true);

  // Footer 마지막 칸에 표시할 값: 연평균 (기존에는 월평균을 표시하여 혼동이 있었음)
  const pfFooterTotal = pfStats.avgYearlyTotal;
  const bmFooterTotal = bmStats.avgYearlyTotal;

  return (
    <div className={styles.heatmapContainer}>
      <div className={styles.heatmapHeader}>
        <div className={styles.heatmapLegend}>
           {isDiffMode ? (
             <>
               <span style={{color: '#2563eb'}}>● Outperform</span>
               <span style={{color: '#d97706'}}>● Underperform</span>
             </>
           ) : (
             <>
                <span style={{color: 'hsl(150, 60%, 45%)'}}>● Gain</span>
                <span style={{color: 'hsl(0, 75%, 55%)'}}>● Loss</span>
                <span>🏆 Beats Market</span>
             </>
           )}
        </div>
      </div>

      <div className={styles.heatmapWrapper}>
        <table className={styles.heatmapTable}>
          <thead>
            <tr>
              <th className={styles.yearCell}>Year</th>
              <th className={styles.typeCell}>Type</th>
              {months.map(m => <th key={m}>{m}</th>)}
              <th className={styles.totalCell}>YTD</th>
            </tr>
          </thead>
          <tbody>
            {years.map(year => {
              const pfRow = pfData[year];
              const bmRow = safeBmData[year] || { returns: Array(12).fill(null), drawdowns: Array(12).fill(null), totalReturn: null };
              
              const pfVals = type === 'return' ? pfRow.returns : pfRow.drawdowns;
              const bmVals = type === 'return' ? bmRow.returns : bmRow.drawdowns;

              const getYearlyMaxDrawdown = (drawdowns) => {
                const validVals = (drawdowns || []).filter(v => v !== null && v !== undefined);
                return validVals.length > 0 ? Math.min(...validVals) : null;
              };

              // --- Difference Mode ---
              if (isDiffMode) {
                const diffVals = pfVals.map((v, i) => {
                  const isNoData = pfRow.returns[i] === null || pfRow.returns[i] === undefined;
                  if (type !== 'return' && isNoData) return null;
                  return (v !== null && bmVals[i] !== null) ? v - bmVals[i] : null;
                });

                const pfTotal = type === 'return' 
                    ? pfRow.totalReturn 
                    : (pfRow.totalReturn === null ? null : getYearlyMaxDrawdown(pfRow.drawdowns));
      
                const bmTotal = type === 'return' 
                    ? bmRow.totalReturn 
                    : (bmRow.totalReturn === null ? null : getYearlyMaxDrawdown(bmRow.drawdowns));
      
                const totalDiff = (pfTotal !== null && bmTotal !== null) ? pfTotal - bmTotal : null;
                
                return (
                  <tr key={year} className={styles.diffRow}>
                    <td className={styles.yearCell}>{year}</td>
                    <td className={styles.typeCell} title="Portfolio - Benchmark">Alpha</td>
                    {diffVals.map((val, idx) => (
                      <td key={idx} style={getCellStyle(val, 'diff')} className={styles.heatmapCell}>
                         <span className={val > 0 ? styles.diffPositive : (val < 0 ? styles.diffNegative : '')}>
                           {formatVal(val)}
                         </span>
                      </td>
                    ))}
                    <td className={styles.totalCell} style={getCellStyle(totalDiff, 'diff')}>
                       <span style={{fontWeight:800}}>{formatVal(totalDiff)}</span>
                    </td>
                  </tr>
                );
              }

              // --- Standard View ---
              const pfTotalDisplay = type === 'return' 
                  ? pfRow.totalReturn 
                  : (pfRow.totalReturn === null ? null : getYearlyMaxDrawdown(pfRow.drawdowns));
              
              const bmTotalDisplay = type === 'return' 
                  ? bmRow.totalReturn 
                  : (bmRow.totalReturn === null ? null : getYearlyMaxDrawdown(bmRow.drawdowns));

              return (
                <React.Fragment key={year}>
                  {/* PF Row */}
                  <tr className={styles.pfRow}>
                    <td rowSpan={2} className={styles.yearCell}>{year}</td>
                    <td className={styles.typeCell} style={{ color: '#2563eb' }}>PF</td>
                    {pfVals.map((val, idx) => {
                      const bmVal = bmVals[idx];
                      const isNoData = pfRow.returns[idx] === null || pfRow.returns[idx] === undefined;
                      const displayVal = (type !== 'return' && isNoData) ? null : val;
                      const isWin = displayVal !== null && bmVal !== null && displayVal > bmVal;

                      return (
                        <td 
                          key={`pf-${idx}`} 
                          style={getCellStyle(displayVal)} 
                          className={`${styles.heatmapCell} ${isWin ? styles.winCell : ''}`}
                          title={isWin ? `Beats BM by ${((displayVal - bmVal)*100).toFixed(1)}%` : ''}
                        >
                          {isWin && <span className={styles.winIcon}>🏆</span>}
                          <span className={styles.pfVal}>{formatVal(displayVal)}</span>
                        </td>
                      );
                    })}
                    <td className={styles.totalCell} style={getCellStyle(pfTotalDisplay)}>
                      {formatVal(pfTotalDisplay)}
                    </td>
                  </tr>
                  
                  {/* BM Row */}
                  <tr className={styles.bmRow}>
                    <td className={styles.typeCell}>BM</td>
                    {bmVals.map((val, idx) => {
                       const isNoData = bmRow.returns[idx] === null || bmRow.returns[idx] === undefined;
                       const displayVal = (type !== 'return' && isNoData) ? null : val;

                       return (
                        <td key={`bm-${idx}`} className={styles.heatmapCell} style={{ opacity: 0.7 }}>
                          <span className={styles.bmVal}>{formatVal(displayVal)}</span>
                        </td>
                       );
                    })}
                    <td className={styles.totalCell} style={{ opacity: 0.7 }}>
                      {formatVal(bmTotalDisplay)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>

          {/* Footer Statistics */}
          <tfoot className={styles.heatmapFooter}>
            <tr className={styles.statRow}>
               <td rowSpan={3} className={styles.avgLabelCell}>STAT</td>
               
               {/* [변경] position: 'sticky', left: '70px', zIndex: 15 제거 */}
               <td className={styles.typeCell} style={{ background:'#f8fafc', borderRight:'1px solid #e2e8f0'}}>WIN%</td>
               
               {pfStats.winRates.map((rate, idx) => (
                 <td key={`wr-${idx}`} className={styles.statValue}>
                   {rate !== null ? (
                     <span className={rate >= 0.6 ? styles.winRateHigh : (rate < 0.4 ? styles.winRateLow : '')}>
                       {(rate * 100).toFixed(0)}%
                     </span>
                   ) : '-'}
                 </td>
               ))}
               <td className={styles.totalCell}>-</td>
            </tr>
            <tr className={styles.statRow}>
              {/* [변경] position: 'sticky' 관련 속성 제거 */}
              <td className={styles.typeCell} style={{ background:'#f8fafc', color:'#2563eb', borderRight:'1px solid #e2e8f0'}}>PF Avg</td>
              
              {pfStats.avgs.map((val, idx) => (
                <td key={`avg-pf-${idx}`} className={styles.statValue}>
                  <strong style={{color: '#334155'}}>{formatVal(val)}</strong>
                </td>
              ))}
              <td className={styles.totalCell}>{formatVal(pfFooterTotal)}</td>
            </tr>
            <tr className={styles.statRow}>
               {/* [변경] position: 'sticky' 관련 속성 제거 */}
               <td className={styles.typeCell} style={{ background:'#f8fafc', borderRight:'1px solid #e2e8f0'}}>BM Avg</td>
               
               {bmStats.avgs.map((val, idx) => (
                 <td key={`avg-bm-${idx}`} className={styles.statValue} style={{color:'#94a3b8'}}>
                   {formatVal(val)}
                 </td>
               ))}
               <td className={styles.totalCell} style={{color:'#94a3b8'}}>{formatVal(bmFooterTotal)}</td>
            </tr>
          </tfoot>

          
          
       
        </table>
      </div>
    </div>
  );
};


const BacktestDetailed = ({ stocks, period = 10, benchmark = "SPY", optimizedWeights, startDate, endDate, rebalancePeriod }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  
  // State
  const [isMobile, setIsMobile] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCrisis, setSelectedCrisis] = useState(null);
  const [showCrisisSection, setShowCrisisSection] = useState(false);
  const [manualZoom, setManualZoom] = useState(null);

  const [activeTab, setActiveTab] = useState({
    cumulative: "portfolioVsBenchmark",
    returnGroup: "portfolioVsBenchmark", returnPeriod: "yearly", returnView: "calendar", rollingPeriod: "1year",
    drawdownGroup: "portfolioVsBenchmark", drawdownPeriod: "yearly", drawdownView: "calendar", rollingDrawdownPeriod: "1year",
    portfolioView: "absolute", table: "portfolio",
    returnViewMode: "visual", 
    drawdownViewMode: "visual"
  });

  // Custom Hook
  const { backtestData, loading, error, getWeightChartData, getProcessedChartData } = useBacktestData({
    stocks, period, benchmark, optimizedWeights, startDate, endDate, rebalancePeriod, t
  });

  useEffect(() => setIsMobile(/Mobi|Android/i.test(navigator.userAgent)), []);
  
  // Handlers
  const updateTab = (key, value) => setActiveTab(prev => ({ ...prev, [key]: value }));
  
  const handleCrisisChange = (e) => {
    const scenarioId = e.target.value;
    setManualZoom(null);
    if (!scenarioId) {
      setSelectedCrisis(null);
    } else {
      const scenario = CRISIS_SCENARIOS.find(s => s.id === scenarioId);
      setSelectedCrisis(scenario);
      setActiveTab(prev => ({ ...prev, returnPeriod: "daily", drawdownPeriod: "daily" }));
    }
  };

  const handleRelayout = (event) => {
    if (event['xaxis.range[0]']) {
      setManualZoom({ start: event['xaxis.range[0]'], end: event['xaxis.range[1]'] });
      setSelectedCrisis(null);
    } else if (event['xaxis.autorange']) {
      setManualZoom(null);
      setSelectedCrisis(null);
    }
  };

  const handleNewPortfolioClick = () => {
    auth.isAuthenticated ? setShowModal(true) : toast.error(<LoginPromptToast signInRedirect={auth.signinRedirect} navigate={navigate} />);
  };

  // Helper
  const renderToggleButton = (key, value, label) => (
    <button 
      onClick={() => updateTab(key, value)} 
      className={`${styles["toggle-button"]} ${activeTab[key] === value ? styles.active : ''}`}
    >
      {label}
    </button>
  );

  // Render Section Helper
  const renderChartSection = (type) => {
    const isCum = type === 'cumulative';
    const isRet = type === 'return';
    const viewKey = isRet ? 'returnView' : 'drawdownView';
    const groupKey = isCum ? 'cumulative' : (isRet ? 'returnGroup' : 'drawdownGroup');
    const periodKey = isRet ? 'returnPeriod' : 'drawdownPeriod';
    
    const viewModeKey = isRet ? 'returnViewMode' : 'drawdownViewMode';

    const { data } = getProcessedChartData(type, activeTab);

    const isDailyOrCrisis = activeTab[periodKey] === 'daily' || selectedCrisis !== null;
    const isDateAxis = isCum || isDailyOrCrisis;

    const isCrisisInPeriod = (crisis) => {
      if (!backtestData.backtestPeriod) return false;
      const dataStart = new Date(backtestData.backtestPeriod.start);
      const dataEnd = new Date(backtestData.backtestPeriod.end);
      const crisisStart = new Date(crisis.start);
      const crisisEnd = new Date(crisis.end);
      return crisisStart <= dataEnd && crisisEnd >= dataStart;
    };
    
    // Zoom & Range Logic
    let activeStart = selectedCrisis?.start || manualZoom?.start;
    let activeEnd = selectedCrisis?.end || manualZoom?.end;
    let yRange = (activeStart && activeEnd) ? calculateYRange(data, activeStart, activeEnd) : null;
    let xRange = (activeStart && activeEnd) ? [activeStart, activeEnd] : null;

    const currentViewMode = isCum ? 'visual' : activeTab[viewModeKey];

    return (
      <div className={styles["backtest-section"]}>
        {isCum && (
            <> 
              <h3>{t("backtest.cumulativeReturn")}</h3>
              <p>{backtestData.backtestPeriod?.start}~{backtestData.backtestPeriod?.end}</p>
              <div>
                  <label className={styles.crisisCheckboxLabel}>
                      <input 
                          type="checkbox" 
                          className={styles.crisisCheckbox} 
                          checked={showCrisisSection} 
                          onChange={(e) => { 
                              setShowCrisisSection(e.target.checked); 
                              if(!e.target.checked) handleCrisisChange({target:{value:""}}); 
                          }} 
                      />
                      <span>🌪️ {t("backtest.historicalCrisisCheck")}</span>
                  </label>
                  
                  <div className={`${styles.crisisPanel} ${showCrisisSection ? styles.panelOpen : ''}`}>
                      <div className={styles.crisisChipWrapper}>
                        <button 
                            className={`${styles.crisisChip} ${!selectedCrisis ? styles.activeChip : ''}`} 
                            onClick={() => handleCrisisChange({ target: { value: "" } })}
                        >
                            {t("backtest.viewFullPeriod")}
                        </button>
                        {CRISIS_SCENARIOS.map(s => {
                            const isInPeriod = isCrisisInPeriod(s);
                            return (
                                <button 
                                    key={s.id} 
                                    disabled={!isInPeriod}
                                    className={`${styles.crisisChip} ${selectedCrisis?.id === s.id ? styles.activeChip : ''}`} 
                                    onClick={() => handleCrisisChange({ target: { value: s.id } })}
                                    title={!isInPeriod ? t("backtest.noDataForPeriod") : ""}
                                >
                                    {t(s.label).split('(')[0]}
                                </button>
                            );
                        })}
                    </div>
                    {selectedCrisis && (
                        <div className={styles.crisisInfoBanner}>
                            <span className={styles.crisisDateRange}>
                                📅 {selectedCrisis.start} ~ {selectedCrisis.end}
                            </span>
                        </div>
                    )}
                  </div>
              </div>
          </>
        )}
        
        {!isCum && <h3>{t(`backtest.${isRet ? 'returnAnalysis' : 'drawdown'}`)}</h3>}
        
        <div className={styles["graph-buttons-container"]}>
           {!isCum && (
              <div className={styles["group-buttons"]}>
                 {renderToggleButton(viewModeKey, 'visual', `📊 ${t("backtest.chart")}`)}
                  {renderToggleButton(viewModeKey, 'table', `📅 ${t("backtest.table")}`)}
              </div>
           )}

           {currentViewMode !== 'table' && (
                <div className={styles["group-buttons"]}>
                    {renderToggleButton(groupKey, 'portfolioVsBenchmark', t("backtest.vsBenchmark"))}
                    {renderToggleButton(groupKey, 'individualStocks', t("backtest.individualStocks"))}
                </div>
            )}
           
           <div className={styles["group-buttons"]}>
                {renderToggleButton('portfolioView', 'absolute', t("backtest.mutualPerformance") || "비교")}
                {renderToggleButton('portfolioView', 'difference', t("backtest.differencePB"))}
           </div>

           {!isCum && currentViewMode === 'visual' && (
               <div className={styles["group-buttons"]}>
                   {renderToggleButton(viewKey, 'calendar', t("backtest.periodReturns"))}
                   {renderToggleButton(viewKey, 'rolling', t("backtest.rollingReturns"))}
               </div>
           )}
        </div>

        {!isCum && currentViewMode === 'visual' && activeTab[viewKey] === 'calendar' && (
             <div className={styles["period-tabs"]}>
                 {['yearly','monthly','daily'].map(p => renderToggleButton(isRet ? 'returnPeriod' : 'drawdownPeriod', p, t(`backtest.${p}`)))}
             </div>
        )}
        {!isCum && currentViewMode === 'visual' && activeTab[viewKey] === 'rolling' && (
             <div className={styles["period-tabs"]}>
                 {['1year','3year','5year'].map(p => renderToggleButton(isRet ? 'rollingPeriod' : 'rollingDrawdownPeriod', p, t(`backtest.${p === '1year' ? 'OneYearRolling' : p === '3year' ? 'ThreeYearRolling' : 'FiveYearRolling'}`)))}
             </div>
        )}

        {currentViewMode === 'visual' ? (
          <Plot
              data={data}
              layout={getCommonLayout(
                  isCum ? t("backtest.cumulativeReturn") : t(`backtest.${isRet ? 'returnPercentage' : 'drawdownPercentage'}`),
                  t("backtest.date"), 
                  isCum ? "Return" : "%",
                  isDateAxis, 
                  yRange, 
                  isMobile, 
                  xRange
              )}
              className={styles["plot-container"]}
              useResizeHandler style={{ width: "100%", height: "100%" }}
              config={{ displayModeBar: false }}
          />
        ) : (
          <div className={styles.heatmapContainer}>
             <ReturnsHeatmap 
               pfData={backtestData?.portfolio?.optimized?.heatmap}
               bmData={backtestData?.benchmark?.heatmap}
               type={type} 
               t={t} 
               viewMode={activeTab.portfolioView}
             />
          </div>
        )}
      </div>
    );
  };

  if (!stocks?.length) return <div>{t("backtest.noPortfolio")}</div>;
  if (loading) return <div className={commonStyles.smallRowLoadingContainer}><div className={commonStyles.spinner}></div>{t("backtest.loadingData")}</div>;
  if (error || !backtestData) return <div className={styles["error-message"]}>{error}</div>;

  const weightsChart = getWeightChartData();

  return (
    <div className={styles.container}>
      <span className={`${commonStyles["data-period-tooltip"]} ${styles.noMarginTooltip}`}>
        ℹ️<span className={commonStyles["tooltip-text"]}>{t("BacktestTooltip")}</span>
      </span>

      {backtestData.dataPeriodInfo?.isPeriodLimited && (
        <div style={{ marginBottom: '20px' }}>
          <DataPeriodAlert dataPeriodInfo={backtestData.dataPeriodInfo} t={t} />
        </div>
      )}

      {renderChartSection('cumulative')}
      {renderChartSection('return')}
      {renderChartSection('drawdown')}

      {weightsChart.data.length > 0 && (
          <div className={styles["backtest-section"]}>
              <h3>{t("backtest.portfolioAllocationHistory")}</h3>
              <Plot
                  data={weightsChart.data}
                  layout={{ ...getCommonLayout(t("backtest.portfolioAllocationHistory"), t("backtest.date"), "Weight (%)", true, [0, 100], isMobile), shapes: weightsChart.shapes }}
                  className={styles["plot-container"]} useResizeHandler style={{ width: "100%", height: "100%" }} config={{ displayModeBar: false }}
              />
          </div>
      )}

      <h3>{t("backtest.Summary")}</h3>
      <div className={styles["toggle-button-group"]}>
         {renderToggleButton('table', 'portfolio', t("backtest.vsBenchmark"))}
         {renderToggleButton('table', 'individual', t("backtest.individualStocks"))}
      </div>
      <div className={styles['table-container']}>
        {activeTab.table === 'portfolio' ? (
          <PerformanceTableBacktest optimizedMetrics={backtestData.portfolio.optimized?.metrics} benchmarkMetrics={backtestData.benchmark?.metrics} benchmarkSymbol={backtestData.benchmark?.symbol} />
        ) : (
          <PerformanceTableIndividual individualStocks={backtestData.individualStocks} benchmarkMetrics={backtestData.benchmark?.metrics} />
        )}
      </div>

      <ChallengeCta stocks={stocks} optimizedWeights={optimizedWeights} />

      {/* <div className={styles.buttonWrapper}>
        <button className={styles.createPortButton} onClick={handleNewPortfolioClick}>
          <span className={styles.icon}>🏆</span><span>{t("backtest.challengeTopOnePercent")}</span>
        </button>
      </div> */}

      <div className={styles.optimizationSection}>
        <h4>{t("backtest.improveTitle") || "포트폴리오 성과 개선하기"}</h4>
        <div className={styles.optimizationGrid}>
            {getOptimizationLinks(t).map(item => (
                <Link key={item.label} to={item.to} className={styles.optimizationCard}>
                    <div className={styles.optIcon}>{item.icon}</div>
                    <div className={styles.optText}><span className={styles.optLabel}>{item.title}</span><span className={styles.optDesc}>{item.desc}</span></div>
                    <ChevronRight size={16} />
                </Link>
            ))}
        </div>
      </div>

      {showModal && auth.isAuthenticated && (
        <NewPortfolioModal onClose={()=>setShowModal(false)} onCreated={(id)=>{setShowModal(false); navigate(`/portfolios/${id}`);}} initialHoldings={stocks.map(ticker => ({ ticker, weight: (parseFloat(optimizedWeights[ticker]) || 0) / 100 }))} />
      )}
      <ToastContainer position="top-center" autoClose={5000} />
    </div>
  );
};

export default BacktestDetailed;